import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PackApiAdapter } from '../adapters/PackApiAdapter';
import { PackService, UpdatePackRequest } from '../services/PackService';

interface UseUpdatePackOptions {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
}

interface UpdatePackVariables {
    id: number;
    data: UpdatePackRequest;
}

export const useUpdatePack = (options: UseUpdatePackOptions = {}) => {
    const queryClient = useQueryClient();
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new PackApiAdapter(httpClient);
    const packService = new PackService(apiAdapter);
    const { toast } = useToast();

    const mutation = useMutation({
        mutationFn: ({ id, data }: UpdatePackVariables) => packService.updatePack(id, data),
        onSuccess: (data, variables) => {
            const { id, data: requestData } = variables;

            // 🎯 Invalidar todas las queries relacionadas con packs
            queryClient.invalidateQueries({ queryKey: ['packs'] });
            queryClient.invalidateQueries({ queryKey: ['packs-simple'] });

            // 🎯 Actualizar el pack específico en el cache solo si hay data
            if (data) {
                queryClient.setQueryData(['pack', id], data);
            }

            // 🎯 Mostrar mensaje de éxito usando el título del request
            toast({
                title: "Pack actualizado",
                description: `El pack "${requestData.title}" se ha actualizado correctamente.`,
                variant: "default",
            });

            // 🎯 Callback personalizado
            options.onSuccess?.();
        },
        onError: (error: Error) => {
            // 🎯 Mostrar mensaje de error
            toast({
                title: "Error al actualizar pack",
                description: error.message || "Ha ocurrido un error inesperado.",
                variant: "destructive",
            });

            // 🎯 Callback personalizado de error  
            options.onError?.(error);
        },
    });

    return {
        updatePack: (id: number, data: UpdatePackRequest) =>
            mutation.mutate({ id, data }),
        updatePackAsync: (id: number, data: UpdatePackRequest) =>
            mutation.mutateAsync({ id, data }),
        isUpdating: mutation.isPending,
        error: mutation.error,
        isSuccess: mutation.isSuccess,
        reset: mutation.reset,
    };
}; 